/**
 * Hero Slider — frontend behaviour.
 *
 * The markup ships Swiper's `.swiper` / `.swiper-wrapper` classes but no
 * `.swiper-slide` (so the editor preview just stacks the slides, all editable).
 * On the frontend we tag each hero-slide `.swiper-slide`, boot Swiper and render
 * the sticker-style numbered dots into `.swiper-pagination`.
 *
 * The editor (window.wp.data) is skipped — the static stack is its preview.
 * A single slide is not a slideshow: Swiper still boots (for consistent layout)
 * but loop/autoplay stay off and the lone dot is hidden via CSS.
 */

import Swiper from "swiper";
import { A11y, Autoplay, Keyboard, Pagination } from "swiper/modules";
import { hydrateLazyVideo } from "@js/partials/lazy-video.js";

// Slide videos are lazy (see assets/js/partials/lazy-video.js). A slide off
// to the side doesn't intersect the viewport, so its video would only start
// loading once the transition is already running: give the next slide its
// source in advance (no playback — the observer starts it when it lands).
function preloadNextSlide(swiper) {
  const rect = swiper.el.getBoundingClientRect();
  if (rect.bottom < 0 || rect.top > window.innerHeight) return;

  const next = swiper.slides[swiper.activeIndex + 1] || swiper.slides[0];
  next?.querySelectorAll("[data-lazy-video]").forEach((video) => {
    // Never the desktop/mobile twin the CSS has hidden.
    if (video.offsetParent !== null) hydrateLazyVideo(video);
  });
}

function initBlock(root) {
  if (root.dataset.heroInited === "1") return;
  root.dataset.heroInited = "1";

  const viewport = root.querySelector(".bskr-hero-slider__viewport");
  const wrapper = root.querySelector(".bskr-hero-slider__track");
  if (!viewport || !wrapper) return;

  const slides = Array.from(
    wrapper.querySelectorAll(":scope > .bskr-hero-slide")
  );
  if (!slides.length) return;
  slides.forEach((s) => s.classList.add("swiper-slide"));

  const multiple = slides.length > 1;
  root.classList.toggle("has-multiple", multiple);

  // eslint-disable-next-line no-new
  new Swiper(viewport, {
    modules: [Pagination, Autoplay, Keyboard, A11y],
    slidesPerView: 1,
    loop: multiple,
    speed: 600,
    grabCursor: multiple,
    autoplay: multiple
      ? { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }
      : false,
    keyboard: { enabled: true },
    pagination: {
      el: root.querySelector(".bskr-hero-slider__dots"),
      clickable: true,
      bulletClass: "bskr-hero-slider__dot",
      bulletActiveClass: "is-active",
      renderBullet(index, className) {
        return (
          `<button type="button" class="${className}" ` +
          `aria-label="Slide ${index + 1}">` +
          `<span class="bskr-hero-slider__dot-shape" aria-hidden="true"></span>` +
          `<span class="bskr-hero-slider__dot-num">${index + 1}</span>` +
          `</button>`
        );
      },
    },
    on: multiple
      ? {
          afterInit: preloadNextSlide,
          slideChangeTransitionEnd: preloadNextSlide,
        }
      : {},
  });
}

function init() {
  // Skip inside the Gutenberg editor — the static stack is the editor preview.
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  document.querySelectorAll(".bskr-hero-slider").forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
